import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { leavePreview } from '../lib/preview';
import { startPageUrl } from '../lib/sites';
import { useBasket } from '../hooks/useBasket';
import { api } from '../lib/api';
import { useInstallPrompt, isInstallPromptDismissed } from '../hooks/useInstallPrompt';
import ProfileDropdown from './ProfileDropdown';
import InstallPromptModal from './InstallPromptModal';

export default function Layout({ children, page, setPage }) {
  const { user, logout } = useAuth();
  const { itemCount, basketTotal, hasUnviewedItems } = useBasket();
  const sendsForApproval = user?.role === 'customer_user' && !user?.permissions?.canPurchaseDirectly;
  const [delivery, setDelivery] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const installPrompt = useInstallPrompt();

  // Used to detect taps outside the hamburger menu so we can close it.
  // Wraps the entire <header> (which contains both the hamburger button
  // and the dropdown list itself) so taps on any header element — basket
  // icon, logo, the hamburger again — don't trigger the close.
  const headerRef = useRef(null);

  useEffect(() => {
    api.getDelivery().then(setDelivery).catch(() => {});
  }, []);

  // Remember the last shopping page (catalog vs order-guide) so the
  // basket page's "Continue Shopping" link can return the customer to
  // where they actually were, instead of always defaulting to the
  // catalog. Only track browse pages — Order History, Feedback, etc.
  // aren't shopping contexts and shouldn't capture the slot.
  useEffect(() => {
    if (page === 'catalog' || page === 'order-guide') {
      try { sessionStorage.setItem('ss_last_browse_page', page); } catch {}
    }
  }, [page]);

  // One-time install nudge: show the modal once on a mobile session if
  // the user isn't already running the PWA and hasn't dismissed before.
  // Delayed a few seconds so it doesn't interrupt the initial page load —
  // first paint should be of the actual catalog/order guide, not a modal.
  useEffect(() => {
    if (installPrompt.isStandalone) return;
    if (!installPrompt.isMobile) return;
    if (isInstallPromptDismissed()) return;
    const timer = setTimeout(() => setInstallModalOpen(true), 2500);
    return () => clearTimeout(timer);
  }, [installPrompt.isStandalone, installPrompt.isMobile]);

  // Close the mobile menu on any tap outside the header. Mirrors the
  // ProfileDropdown's outside-click handling. The Escape key also
  // closes — small accessibility nicety.
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  const nav = (p) => { window.location.hash = p; setPage(p); setMenuOpen(false); };

  // A staff preview (pages/StaffPreview.jsx) signs out back to the staff
  // sites rather than to the customer login.
  const preview = user?.preview;
  const endPreview = () => { leavePreview(user); window.location.replace(startPageUrl()); };
  const signOut = preview ? endPreview : logout;

  const nextDelivery = delivery?.nextDelivery
    ? new Date(delivery.nextDelivery).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    : null;

  const cutoff = delivery?.cutoffDate
    ? new Date(delivery.cutoffDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : null;

  return (
    <div style={styles.root}>
      {/* Top nav */}
      <header ref={headerRef} style={styles.header}>
        {preview && (
          <div data-staff-preview-banner style={styles.previewBar}>
            <span>
              <strong>Staff preview.</strong> You're seeing the portal as <strong>{user.customerName}</strong>
              {user.priceTierName ? ` (${user.priceTierName} pricing)` : ''}. Look only: nothing can be ordered or changed.
            </span>
            <span style={styles.previewActions}>
              <button type="button" style={styles.previewBtn} onClick={() => nav('staff-preview')}>Switch customer</button>
              <button type="button" style={styles.previewBtn} onClick={endPreview}>End preview</button>
            </span>
          </div>
        )}
        <div style={styles.headerInner}>
          <button
            type="button"
            style={styles.logo}
            onClick={() => nav('catalog')}
            aria-label="Go to catalog"
            title="Go to catalog"
          >
            <img src="/logo.png" alt="Silver Service" style={styles.logoImg} />
          </button>

          {/* Desktop nav */}
          <nav style={styles.nav}>
            <button style={navBtn(page === 'catalog')} onClick={() => nav('catalog')}>
              Catalog
            </button>
            <button style={navBtn(page === 'order-guide')} onClick={() => nav('order-guide')}>
              Order Guide
            </button>
            <button style={navBtn(page === 'specials')} onClick={() => nav('specials')}>
              🔥 Specials
            </button>
            <button style={navBtn(page === 'order-history')} onClick={() => nav('order-history')}>
              Order History
            </button>
            {user?.features?.invoices && (
              <button style={navBtn(page === 'invoices')} onClick={() => nav('invoices')}>
                Invoices
              </button>
            )}
            <button style={navBtn(page === 'service-request')} onClick={() => nav('service-request')}>
              Service Request
            </button>
            {user?.role === 'customer_admin' && (
              <button style={navBtn(page === 'approvals')} onClick={() => nav('approvals')}>
                Approvals
              </button>
            )}
            {sendsForApproval && (
              <button style={navBtn(page === 'approvals')} onClick={() => nav('approvals')}>
                Pending Orders
              </button>
            )}
            <button style={navBtn(page === 'feedback')} onClick={() => nav('feedback')}>
              Give Feedback
            </button>
          </nav>

          <div style={styles.headerRight}>
            {/* Basket indicator */}
            <button style={styles.basketBtn} onClick={() => nav('basket')}>
              <span style={styles.basketIcon}>🛒</span>
              {itemCount > 0 && (
                <span style={styles.basketBadge}>{itemCount}</span>
              )}
              {itemCount > 0 && (
                <span data-basket-amount style={styles.basketAmount}>${basketTotal.toFixed(2)}</span>
              )}
            </button>

            {/* Profile dropdown */}
            <ProfileDropdown
              user={user}
              onNavigate={nav}
              onLogout={signOut}
              showInstall={installPrompt.isMobile && !installPrompt.isStandalone}
              onInstallClick={() => setInstallModalOpen(true)}
            />

            {/* Mobile menu */}
            <button data-hamburger style={styles.hamburger} onClick={() => setMenuOpen(!menuOpen)}>☰</button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={styles.mobileMenu}>
            <button style={styles.mobileMenuItem} onClick={() => nav('catalog')}>Catalog</button>
            <button style={styles.mobileMenuItem} onClick={() => nav('order-guide')}>Order Guide</button>
            <button style={styles.mobileMenuItem} onClick={() => nav('specials')}>🔥 Specials</button>
            <button style={styles.mobileMenuItem} onClick={() => nav('order-history')}>Order History</button>
            {user?.features?.invoices && (
              <button style={styles.mobileMenuItem} onClick={() => nav('invoices')}>Invoices</button>
            )}
            <button style={styles.mobileMenuItem} onClick={() => nav('service-request')}>Service Request</button>
            {user?.role === 'customer_admin' && (
              <button style={styles.mobileMenuItem} onClick={() => nav('approvals')}>Approvals</button>
            )}
            {sendsForApproval && (
              <button style={styles.mobileMenuItem} onClick={() => nav('approvals')}>Pending Orders</button>
            )}
            <button style={styles.mobileMenuItem} onClick={() => nav('request')}>Request a Product</button>
            <button style={styles.mobileMenuItem} onClick={() => nav('feedback')}>Give Feedback</button>
            {itemCount > 0 && (
              <button style={styles.mobileMenuItem} onClick={() => nav('basket')}>
                Basket ({itemCount} items · ${basketTotal.toFixed(2)})
              </button>
            )}
            <button style={styles.mobileMenuItem} onClick={signOut}>{preview ? 'End preview' : 'Sign Out'}</button>
          </div>
        )}
      </header>

      {/* Delivery banner — only shown when we actually have a next-delivery
          date. Placeholder "Contact us for delivery schedule" state removed;
          customers whose delivery schedule isn't set up see no banner at all
          instead of an unhelpful empty strip. */}
      {delivery && nextDelivery && (
        <div data-delivery-banner style={styles.deliveryBanner}>
          <div style={styles.deliveryInner}>
            <span style={styles.deliveryIcon}>🚚</span>
            <span>Next delivery: <strong>{nextDelivery}</strong></span>
            {cutoff && <span style={styles.deliverySep}>·</span>}
            {cutoff && <span>Order by <strong>{cutoff} at {delivery.cutoffTime}</strong></span>}
            {delivery.shippingMethod === 'fedex' && (
              <><span style={styles.deliverySep}>·</span><span style={styles.fedexBadge}>FedEx</span></>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <main style={styles.main}>
        {children}
      </main>

      {/* Site footer — legal + help links, shown on every page. */}
      <footer style={styles.footer}>
        <div style={styles.footerLinks}>
          <a style={styles.footerLink} href="/help" target="_blank" rel="noopener">Help &amp; Quick Start</a>
          <span style={styles.footerSep}>·</span>
          <a style={styles.footerLink} href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>
          <span style={styles.footerSep}>·</span>
          <a style={styles.footerLink} href="/terms" target="_blank" rel="noopener">Terms of Use</a>
        </div>
        <div style={styles.footerCopy}>© {new Date().getFullYear()} Silver Service Refreshment Systems</div>
      </footer>

      {/* "Can't find a product?" lives in the Catalog page header (where
          product-hunting happens) and in the mobile hamburger menu — not as a
          floating button, which collided with the sticky Add-to-Basket bar. */}

      {/* PWA install nudge — auto-shown on first mobile session, also
          openable from the profile dropdown. */}
      <InstallPromptModal
        open={installModalOpen}
        onClose={() => setInstallModalOpen(false)}
        prompt={installPrompt}
      />

      {/* Mobile sticky basket bar — only visible on mobile (CSS-gated),
          only renders when there are unviewed items (added since the user
          last opened the basket page) and the user is on a non-basket
          page. Hides after the basket has been viewed once; reappears
          when a new item is added. */}
      {itemCount > 0 && hasUnviewedItems && page !== 'basket' && (
        <button
          data-mobile-basket-bar
          style={styles.mobileBasketBar}
          onClick={() => nav('basket')}
          aria-label={`View basket: ${itemCount} item${itemCount === 1 ? '' : 's'}, $${basketTotal.toFixed(2)} subtotal`}
        >
          <span style={styles.mobileBasketIcon}>🛒</span>
          <span style={styles.mobileBasketCount}>
            {itemCount} item{itemCount === 1 ? '' : 's'}
          </span>
          <span style={styles.mobileBasketTotal}>${basketTotal.toFixed(2)}</span>
          <span style={styles.mobileBasketCta}>View →</span>
        </button>
      )}
    </div>
  );
}

const navBtn = (active) => ({
  background: 'none',
  border: 'none',
  color: active ? '#A50B18' : 'rgba(0,0,0,0.55)',
  borderBottom: active ? '2px solid #A50B18' : '2px solid transparent',
  padding: '0.5rem 1rem',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: active ? '600' : '400',
  transition: 'all 0.15s',
});

const styles = {
  root: { minHeight: '100vh', background: '#f5f5f5', color: '#333', fontFamily: "'Inter', sans-serif" },
  header: { background: '#fff', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  previewBar: { background: '#2b2b2b', color: '#fff', fontSize: '0.85rem', lineHeight: 1.4, padding: '0.55rem 1.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem 1rem' },
  previewActions: { display: 'flex', gap: '0.5rem', flexShrink: 0 },
  previewBtn: { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.45)', borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  headerInner: { maxWidth: 1600, margin: '0 auto', padding: '0 1.5rem', height: 60, display: 'flex', alignItems: 'center', gap: '2rem' },
  logo: { display: 'flex', alignItems: 'center', marginRight: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' },
  logoImg: { height: 50, width: 'auto' },
  nav: { display: 'flex', gap: '0.5rem' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '1rem', marginLeft: 'auto' },
  basketBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '0.3rem',
    padding: '0.3rem 0.5rem', borderRadius: 6,
    position: 'relative',
    transition: 'background 0.15s',
  },
  basketIcon: { fontSize: '1.1rem' },
  basketBadge: {
    background: '#A50B18', color: '#fff',
    fontSize: '0.65rem', fontWeight: 700,
    width: 18, height: 18, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'absolute', top: -2, right: -4,
  },
  basketAmount: {
    fontSize: '0.8rem', color: '#A50B18', fontWeight: 600,
    marginLeft: '0.3rem',
  },
  hamburger: { display: 'none', background: 'none', border: 'none', color: '#333', fontSize: '1.3rem', cursor: 'pointer' },
  mobileMenu: { background: '#fff', borderTop: '1px solid #e0e0e0', padding: '0.5rem 0' },
  mobileMenuItem: { display: 'block', width: '100%', padding: '0.8rem 1.5rem', background: 'none', border: 'none', color: '#333', textAlign: 'left', cursor: 'pointer', fontSize: '0.95rem' },
  deliveryBanner: { background: '#fef2f2', borderBottom: '1px solid #fecaca' },
  deliveryInner: { maxWidth: 1600, margin: '0 auto', padding: '0.6rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem', color: 'rgba(0,0,0,0.65)', flexWrap: 'wrap' },
  deliveryIcon: { fontSize: '1rem' },
  deliverySep: { color: 'rgba(0,0,0,0.2)' },
  fedexBadge: { background: '#4d1a8c', color: '#fff', padding: '0.1rem 0.5rem', borderRadius: 3, fontSize: '0.75rem', fontWeight: 600 },
  main: { maxWidth: 1600, margin: '0 auto', padding: '2rem 1.5rem', paddingBottom: '6rem' },
  footer: {
    borderTop: '1px solid #ececec',
    padding: '1.5rem',
    textAlign: 'center',
    color: 'rgba(0,0,0,0.4)',
    fontSize: '0.8rem',
  },
  footerLinks: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  footerLink: { color: 'rgba(0,0,0,0.55)', textDecoration: 'none' },
  footerSep: { color: 'rgba(0,0,0,0.25)' },
  footerCopy: { marginTop: '0.5rem' },
  // Mobile sticky basket bar. Hidden by default (CSS in index.html
  // unhides it at <=768px). When visible: full-width strip at the
  // bottom of the screen, padded for the iPhone home indicator.
  mobileBasketBar: {
    display: 'none',
    position: 'fixed', left: 0, right: 0, bottom: 0,
    background: '#A50B18', color: '#fff',
    border: 'none',
    padding: '0.85rem 1.1rem calc(0.85rem + env(safe-area-inset-bottom, 0)) 1.1rem',
    width: '100%',
    fontFamily: 'inherit', fontSize: '0.95rem', fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 -2px 12px rgba(0,0,0,0.18)',
    zIndex: 98,
    alignItems: 'center', gap: '0.65rem',
  },
  mobileBasketIcon: { fontSize: '1.25rem', lineHeight: 1 },
  mobileBasketCount: { whiteSpace: 'nowrap' },
  mobileBasketTotal: { fontFamily: 'monospace', fontWeight: 700, marginLeft: 'auto' },
  mobileBasketCta: { opacity: 0.85, marginLeft: '0.4rem', whiteSpace: 'nowrap' },
};
